import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning';
  is_read: boolean;
  created_at: string;
}

async function fetchWithAuth(url: string, options: RequestInit = {}, token: string) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function useNotifications() {
  const { token, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/crm/notifications`,
        {},
        token
      );
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  const deleteNotification = useCallback(async (id: string) => {
    if (!token) return;
    try {
      await fetchWithAuth(
        `${API_URL}/api/crm/notifications/${id}`,
        { method: 'DELETE' },
        token
      );
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete notification',
      });
    }
  }, [token, toast]);

  const clearAllNotifications = useCallback(async () => {
    if (!token) return;
    try {
      await fetchWithAuth(
        `${API_URL}/api/crm/notifications`,
        { method: 'DELETE' },
        token
      );
      setNotifications([]);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to clear notifications',
      });
    }
  }, [token, toast]);

  const createNotification = useCallback(async (title: string, message: string, type: string = 'info') => {
    if (!token) return;
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/crm/notifications`,
        {
          method: 'POST',
          body: JSON.stringify({ title, message, type }),
        },
        token
      );
      setNotifications(prev => [data, ...prev]);
    } catch (err) {
      console.error('Failed to create notification:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token && isAuthenticated) {
      fetchNotifications();
    }
  }, [token, isAuthenticated, fetchNotifications]);

  return {
    notifications,
    isLoading,
    fetchNotifications,
    deleteNotification,
    clearAllNotifications,
    createNotification,
  };
}
